import axios from 'utils/axios';
import type {
    ClientContactItemType,
    ClientContactWriteType,
    ClientCustomerType,
    GetClientCustomerListResultType,
    GetClientListResultType,
    ObjectCustomerType,
} from '@econtractor/types';

export interface ClientListParams {
    page?: number;
    onPage?: number;
    search?: string;
    order?: string;
    status?: number[] | null;
}

export interface ClientCustomerListParams {
    page?: number;
    onPage?: number;
    search?: string;
    order?: string;
    clientId?: number | null;
    vatEnabled?: 0 | 1 | null;
}

export interface ClientPayload {
    clientName?: string;
    clientEmail?: string;
    clientPhone?: string;
    clientAddress?: string;
    clientLocation?: string;
    clientDescription?: string;
    clientStatus?: number;
    contacts?: ClientContactWriteType[];
}

export const getAllClients = async () => {
    try {
        const response = await axios('/clients/all');
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getList = async (getData: ClientListParams): Promise<GetClientListResultType | null> => {
    try {
        const response = await axios('/clients', { params: getData });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getClientById = async (clientId: number | string) => {
    try {
        const response = await axios(`/clients/${clientId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export type CreateClientResult =
    | { ok: true; clientId: number }
    | { ok: false; errCode?: string };

export const createClient = async (createData: ClientPayload): Promise<CreateClientResult> => {
    try {
        const response = await axios.post('/clients/new', createData);
        const clientId = response?.data?.clientId;
        if (!clientId) return { ok: false };
        return { ok: true, clientId };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const updateClient = async (clientId: number | string, clientData: ClientPayload) => {
    try {
        const response = await axios.post(`/clients/${clientId}`, clientData);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const deleteClient = async (clientId: number | string) => {
    try {
        const response = await axios.delete(`/clients/${clientId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const setClientField = async (
    clientId: number | string,
    fieldName: string,
    fieldValue: string,
) => {
    try {
        const response = await axios.post(`/clients/field/${clientId}`, { fieldName, fieldValue });
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const getCustomersList = async (
    getData: ClientCustomerListParams,
): Promise<GetClientCustomerListResultType | null> => {
    try {
        const response = await axios('/clients/customers', {
            params: {
                ...getData,
                clientId: getData.clientId || undefined,
                vatEnabled:
                    getData.vatEnabled === 0 || getData.vatEnabled === 1
                        ? getData.vatEnabled
                        : undefined,
            },
        });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getClientCustomers = async (
    clientId: number | string,
): Promise<ClientCustomerType[] | null> => {
    try {
        const response = await axios(`/clients/${clientId}/customers`);
        return Array.isArray(response?.data) ? response.data : null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getClientCustomer = async (
    clientId: number | string,
    customerId: number | string,
): Promise<ClientCustomerType | null> => {
    try {
        const response = await axios(`/clients/${clientId}/customers/${customerId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export type SaveClientCustomerResult =
    | { ok: true; id?: number }
    | { ok: false; errCode?: string };

export const upsertClientCustomer = async (
    clientId: number | string,
    customer: ObjectCustomerType,
): Promise<SaveClientCustomerResult> => {
    try {
        const response = await axios.post(`/clients/${clientId}/customers`, { customer });
        if (response?.data?.stat) return { ok: true, id: response.data.id };
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const updateClientCustomer = async (
    clientId: number | string,
    customerId: number | string,
    customer: ObjectCustomerType,
    targetClientId?: number | string | null,
): Promise<SaveClientCustomerResult> => {
    try {
        const response = await axios.post(`/clients/${clientId}/customers/${customerId}`, {
            customer,
            clientId: targetClientId ?? undefined,
        });
        if (response?.data?.stat) return { ok: true };
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const deleteClientCustomer = async (
    clientId: number | string,
    customerId: number | string,
): Promise<boolean> => {
    try {
        const response = await axios.delete(`/clients/${clientId}/customers/${customerId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const getClientContacts = async (
    clientId: number | string,
): Promise<ClientContactItemType[] | null> => {
    try {
        const response = await axios(`/clients/${clientId}/contacts`);
        return Array.isArray(response?.data) ? response.data : null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export type SaveClientContactResult =
    | { ok: true; contact: ClientContactItemType }
    | { ok: false; errCode?: string };

export const addClientContact = async (
    clientId: number | string,
    contact: ClientContactWriteType,
): Promise<SaveClientContactResult> => {
    try {
        const response = await axios.post(`/clients/${clientId}/contacts`, contact);
        if (response?.data?.stat && response?.data?.contact) {
            return { ok: true, contact: response.data.contact };
        }
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const updateClientContact = async (
    clientId: number | string,
    contactId: number | string,
    contactValue: string,
): Promise<SaveClientContactResult> => {
    try {
        const response = await axios.post(`/clients/${clientId}/contacts/${contactId}`, { contactValue });
        if (response?.data?.stat && response?.data?.contact) {
            return { ok: true, contact: response.data.contact };
        }
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const deleteClientContact = async (
    clientId: number | string,
    contactId: number | string,
): Promise<boolean> => {
    try {
        const response = await axios.delete(`/clients/${clientId}/contacts/${contactId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};
